import {Component, Input, OnDestroy, OnInit} from '@angular/core';
import {animate, state, style, transition, trigger} from "@angular/animations";
import {NavigationService} from "../navigation.service";
import {ActivatedRoute, Router} from "@angular/router";
import {FormControl, Validators} from "@angular/forms";
import {TextUtils} from "../../../../../shared/utils/text-utils";
import {INavigationType} from "../navigation.interface";
import {NotifierComponent} from "../../../../../shared/components/notifier/notifier.component";
import {Notification} from "../../../../../shared/components/notifier/notification";
import {NotificationType} from "../../../../../shared/components/notifier/notification-type";
import {NavigationInteractionsService} from "../navigation-interactions.service";
import {RemoveItemDialogComponent} from "../../products/remove-item-dialog/remove-item-dialog.component";
import {DialogService} from "../../../../../../ng2-bootstrap-modal/dialog.service";
import {DbTextPipe} from "../../../../../shared/pipes/db-text.pipe";
import {first} from "rxjs/operators";
import {DbModalService} from "../../../../../modules/modal/modal";

interface IFullMenu {
  id: number;
  items: INavigationType[];
  name: string;
  slug: string;
}

@Component({
    selector: 'db-add-ecommerce-menu',
    templateUrl: './add-ecommerce-menu.component.html',
    styleUrls: ['./add-ecommerce-menu.component.scss'],
    animations: [
        trigger('menuSlider', [
            state('open', style({
                width: '300px', // Set the width to 30% when open
            })),
            state('closed', style({
                width: '0', // Set the width to 0 when closed
            })),
            transition('open <=> closed', [
                animate('0.3s ease-out'),
            ]),
        ]),
    ],
    standalone: false
})
export class AddEcommerceMenuComponent implements OnInit, OnDestroy {
  menuState = 'closed';
  menuNameControl = new FormControl(null, [Validators.required, Validators.pattern(/.*\S.*/)]);
  website = this._activatedRoute.snapshot.parent.parent.data['website'] || this._activatedRoute.snapshot.parent.data['website'];
  defaultValue = {
    name: '',
    items: []
  };
  menuId = this._activatedRoute.snapshot.params['menuId'];
  loading = false;
  saved = false;
  @Input() isDemo = false;

  constructor(private _navigationService: NavigationService,
              private _router: Router,
              private _dialogService: DialogService,
              private _dbText: DbTextPipe,
              private _modalService: DbModalService,
              private _navigationInteractionsService: NavigationInteractionsService,
              private _activatedRoute: ActivatedRoute) {
  }

  ngOnInit() {
    if (this.menuId || this.isDemo) {
      this.getMenu();
    }
  }

  toggleMenu() {
    this.menuState = this.menuState === 'closed' ? 'open' : 'closed';
  }

  getMenu() {
    this.loading = true;
    this._navigationService.getMenu(this.website.id, this.menuId)
      .subscribe((response) => {
        if (response) {
          if (response.success) {
            const data: IFullMenu = response.data;
            this.defaultValue.name = data.name;
            this.defaultValue.items = JSON.parse(JSON.stringify(this._navigationInteractionsService.transformToLocal(data.items)));
            this._setMenu();
          }
          this.loading = false;
        }
      });
  }

  closeSidebar() {
    if (this.menuState === 'closed') {
      return;
    }
    this.menuState = 'closed';
  }

  navigateBack() {
    this._router.navigate(['.'], {relativeTo: this._activatedRoute.parent});
  }

  onDiscard() {
    if (!this._navigationInteractionsService.hasChanges.value && this.menuNameControl.pristine) {
      this.navigateBack();
      return;
    }
    this._openLeavePopup();
  }

  private _openLeavePopup() {
    const alternativePopup = {
      title: "Are you sure you want to leave?",
      description: "Changes you made won’t be saved.",
      button: "Leave"
    };
    const modal = this._modalService.open(RemoveItemDialogComponent);
    modal.componentInstance.alternativePopup = alternativePopup;
    modal.closed
      .subscribe(value => {
        if (value) {
          if (this.saved || this.menuId) {
            if (this._navigationInteractionsService.hasChanges.value || this.menuNameControl.dirty) {
              this._setMenu();
              return;
            }
          } else {
            this.navigateBack();
          }
        }
      });
  }

  private _setMenu() {
    this.menuNameControl.setValue(this._navigationService.decodeHtml(this.defaultValue.name));
    this._navigationInteractionsService.navigationItems.next(JSON.parse(JSON.stringify(this.defaultValue.items)));
    this._navigationInteractionsService.hasChanges.next(false);
    this.menuNameControl.markAsPristine();
  }

  onSave() {
    if (this.menuNameControl.invalid) {
      this.menuNameControl.markAsTouched();
      return;
    }
    this.menuNameControl.markAsPristine();
    this._navigationService.sendAnalyticsEvent('Save', this.website.id);
    this.loading = true;
    const items = this._navigationInteractionsService.transformForRequestArray(this._navigationInteractionsService.navigationItems.value);
    const data = {name: this.menuNameControl.value, items};
    const action = this.menuId ? this.updateMenu : this.addMenu;
    action.bind(this, data)();
  }

  addMenu(data) {
    this._navigationService.addMenu(this.website.id, {data})
      .pipe(first(resp => !!resp))
      .subscribe(resp => {
        if (resp) {
          this.loading = false;
          if (resp.success) {
            this.saved = true;
            const value: IFullMenu = resp.data;
            this.menuId = value.id;
            this.defaultValue.name = value.name;
            this.defaultValue.items = JSON.parse(JSON.stringify(this._navigationInteractionsService.transformToLocal(value.items)));
            this._setMenu();
            this._router.navigate(['../edit-menu', this.menuId], {
              relativeTo: this._activatedRoute,
            });
            NotifierComponent.notify(new Notification(TextUtils.getText('text_newMenuAdded'), NotificationType.SUCCESS));
          } else {
            this._showErrorNotif(resp.message);
          }
        }
      });
  }

  updateMenu(data) {
    this._navigationService.updateMenu(this.website.id, {data}, this.menuId)
      .pipe(first(resp => !!resp))
      .subscribe(resp => {
        if (resp) {
          this.loading = false;
          if (resp.success) {
            this.saved = true;
            const value: IFullMenu = resp.data;
            this.defaultValue.name = value.name;
            this.defaultValue.items = JSON.parse(JSON.stringify(this._navigationInteractionsService.transformToLocal(value.items)));
            this._setMenu();
            NotifierComponent.notify(new Notification(TextUtils.getText('text_menuUpdated'), NotificationType.SUCCESS));
          } else {
            this._showErrorNotif(resp.message);
          }
        }
      });
  }

  private _showErrorNotif(mess: string) {
    const message = mess === 'menu_exists' ? this._dbText.transform(mess) : "Something went wrong";
    NotifierComponent.notify(new Notification(message, NotificationType.DANGER));
  }

  ngOnDestroy() {
    this._navigationInteractionsService.navigationItems.next([]);
  }
}
